import { useState } from "react";
import {
  ArrowLeft,
  LockSimple,
  LockSimpleOpen,
  PencilSimple,
  Sliders,
} from "@phosphor-icons/react";
import { useNavigate, useParams } from "react-router-dom";

import { ActivityLogTab, NotesTab, UserAccessTab } from "@/components";
import { useUsers } from "@/hooks";
import { MODAL_ROUTES } from "@/router";
import { useNavigateModal } from "@/router/useNavigateModal";
import { Button, Heading, Loader, Tabs } from "@/ui";
import type { Tab } from "@/ui";
import { formatDateToDDMMYYYY, tw } from "@/utils";
import { mapPlanNameToLabel } from "@/utils/planUtils";

const Field = ({
  label,
  wide,
  children,
}: {
  label: string;
  wide?: boolean;
  children: React.ReactNode;
}) => (
  <div
    className={tw("flex flex-col gap-1", wide && "sm:col-span-2 lg:col-span-3")}
  >
    <span className="text-xs font-medium uppercase tracking-wide text-neutral-400">
      {label}
    </span>
    <span className="text-sm text-neutral-900">{children}</span>
  </div>
);

export const UserDetails = () => {
  const { userId } = useParams();
  const navigate = useNavigate();
  const navigateModal = useNavigateModal();
  const { useUser } = useUsers();

  const { data: user, isLoading } = useUser(
    userId ? String(userId) : undefined,
  );

  const [currentTabLabel, setCurrentTabLabel] = useState<string>("Details");

  if (isLoading)
    return (
      <div className="flex h-full items-center justify-center">
        <Loader />
      </div>
    );

  if (!user)
    return (
      <div className="p-4 sm:p-6 lg:p-8">
        <Heading level={2} heading="User not found" />
        <Button onClick={() => navigate(-1)} className="mt-4">
          Go back
        </Button>
      </div>
    );

  const isVerified = !!user.email_verified_at;

  const UserInfoView = () => (
    <div className="grid grid-cols-1 gap-x-10 gap-y-7 pt-6 sm:grid-cols-2 lg:grid-cols-3">
      <Field label="Email">{user.email}</Field>
      <Field label="Country">{user.country || "—"}</Field>
      <Field label="Role">
        <span className="capitalize">{user.user_type || "User"}</span>
      </Field>
      <Field label="Email verified">{isVerified ? "Yes" : "No"}</Field>
      <Field label="Joined">
        {user.created_at
          ? formatDateToDDMMYYYY(new Date(user.created_at))
          : "—"}
      </Field>
      <Field label="Last updated">
        {user.updated_at
          ? formatDateToDDMMYYYY(new Date(user.updated_at))
          : "—"}
      </Field>
      <Field label="Subscriptions" wide>
        {user.memberships?.length
          ? user.memberships.map((name) => mapPlanNameToLabel(name)).join(", ")
          : "None"}
      </Field>
      <Field label="Partners" wide>
        {user.partners?.length
          ? user.partners.map((p) => p.name).join(", ")
          : "Not linked to a partner"}
      </Field>
    </div>
  );

  const tabs: Tab[] = [
    {
      label: "Details",
      component: <UserInfoView />,
      roles: ["admin"],
    },
    {
      label: "Access",
      component: <UserAccessTab user={user} />,
      roles: ["admin"],
    },
    {
      label: "Activity Log",
      component: userId ? (
        <ActivityLogTab subjectType="user" subjectId={userId} />
      ) : null,
      roles: ["admin"],
    },
    {
      label: "Notes",
      component: userId ? (
        <NotesTab
          endpoint={`/users/${userId}/notes`}
          queryKey={["notes", "user", userId]}
        />
      ) : null,
      roles: ["admin"],
    },
  ];

  return (
    <div className="flex h-full flex-col gap-8 p-4 sm:p-6 lg:p-8">
      <button
        type="button"
        onClick={() => navigate(-1)}
        className="flex w-fit items-center gap-2 text-sm font-medium text-neutral-400 hover:text-primary-700"
      >
        <ArrowLeft size={18} />
        Back
      </button>

      {/* Header */}
      <div className="flex flex-col gap-5 sm:flex-row sm:items-start sm:justify-between">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-3">
            <Heading
              level={1}
              heading={
                `${user.first_name ?? ""} ${user.last_name ?? ""}`.trim() ||
                user.email
              }
            />
            {user.locked ? (
              <span className="rounded-full bg-error-50 px-2.5 py-0.5 text-xs font-semibold text-error-700">
                Locked
              </span>
            ) : null}
          </div>
          <p className="mt-1 truncate text-sm text-neutral-500">{user.email}</p>
        </div>

        <div className="flex flex-wrap gap-2">
          <Button
            size="sm"
            onClick={() => navigateModal(MODAL_ROUTES.editUser, { user })}
          >
            <PencilSimple size={16} />
            Edit
          </Button>
          <Button
            size="sm"
            variant="outlined"
            disabled={user.locked}
            onClick={() =>
              navigateModal(MODAL_ROUTES.manageUserAccessLevel, { user })
            }
          >
            <Sliders size={16} />
            Manage access
          </Button>
          <Button
            size="sm"
            variant="outlined"
            onClick={() => navigateModal(MODAL_ROUTES.lockUser, { user })}
          >
            {user.locked ? (
              <LockSimpleOpen size={16} />
            ) : (
              <LockSimple size={16} />
            )}
            {user.locked ? "Unlock" : "Lock"}
          </Button>
        </div>
      </div>

      <Tabs
        tabs={tabs}
        userRole="admin"
        currentLabel={currentTabLabel}
        setCurrentLabel={setCurrentTabLabel}
      />
    </div>
  );
};
